import { useEffect, useRef } from 'react'

type Rgb = [number, number, number]

type Palette = {
  base: Rgb
  sunset: Rgb[]
  contrast: number
  stroke: number
  accent: string
}

const DEFAULT_PALETTE = {
  base: [175, 175, 175] as Rgb,
  sunset: [
    [175, 175, 175],
    [150, 150, 150],
    [205, 205, 205],
    [235, 235, 235],
  ] as Rgb[],
  contrast: 1,
  stroke: 1,
}

const SPEED = 116
const PERIOD = 1900

function readRgb(value: string, fallback: Rgb): Rgb {
  const parsed = value.split(',').map(Number)
  return parsed.length === 3 && parsed.every(Number.isFinite) ? [parsed[0], parsed[1], parsed[2]] : fallback
}

function readPalette(canvas: HTMLCanvasElement): Palette {
  const styles = getComputedStyle(canvas)
  return {
    base: readRgb(styles.getPropertyValue('--motion-rgb').trim(), DEFAULT_PALETTE.base),
    sunset: ['periwinkle', 'lilac', 'orange', 'mango'].map((name, index) =>
      readRgb(styles.getPropertyValue(`--sunrise-${name}-rgb`).trim(), DEFAULT_PALETTE.sunset[index]),
    ),
    contrast: Number(styles.getPropertyValue('--motion-contrast')) || DEFAULT_PALETTE.contrast,
    stroke: Number(styles.getPropertyValue('--motion-stroke')) || DEFAULT_PALETTE.stroke,
    accent: DEFAULT_PALETTE.base.join(','),
  }
}

// Decorative source-footage timeline used by the deployed Motion access screen.
// It intentionally has no audio, scroll, or input behavior of its own.
export function MotionTimeline({ fullHeight = false }: { fullHeight?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const context = canvas.getContext('2d')
    if (!context) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let frame: number | null = null
    let width = 0
    let height = 0
    let time = 0
    let previous = 0
    let palette = readPalette(canvas)

    const green = (alpha: number, tone: keyof Pick<Palette, 'base'> = 'base') => {
      const rgb = palette[tone]
      return `rgba(${rgb.join(',')},${Math.min(1, alpha * palette.contrast)})`
    }

    const hash = (value: number) => {
      const x = Math.sin(value * 127.1) * 43758.5453
      return x - Math.floor(x)
    }

    const level = (position: number, currentTime: number) => .30 + .24 * Math.sin(position / 190 + 1.2) + .16 * Math.sin(position / 71) + .11 * Math.sin(position / 29 + .6)
      + .07 * Math.sin(position / 13) + .06 * Math.sin(position / 23 + currentTime * 3.1) + .04 * Math.sin(position / 7 - currentTime * 4.3)

    const momentAt = (position: number) => {
      const take = Math.floor(position / PERIOD)
      const local = position - take * PERIOD
      for (let index = 0; index < 3; index += 1) {
        const start = (.06 + index * .31 + hash(take * 7 + index) * .12) * PERIOD
        if (local >= start && local <= start + (.11 + hash(take * 13 + index) * .07) * PERIOD) return true
      }
      return false
    }

    const draw = (currentTime: number) => {
      const stops = [0, 1, 2, 3, 2, 1]
      const cycle = (offset: number) => {
        const phase = (currentTime / 6 + offset) % stops.length
        const index = Math.floor(phase)
        const blend = phase - index
        const from = palette.sunset[stops[index]]
        const to = palette.sunset[stops[(index + 1) % stops.length]]
        return from.map((value, channel) => Math.round(value + (to[channel] - value) * blend)).join(',')
      }

      palette.accent = cycle(0)
      context.clearRect(0, 0, width, height)
      if (width < 40 || height < 40) return

      const visible = Math.min(height, window.innerHeight || height)
      const tall = visible / width > 1.2
      const mid = height / 2
      const peak = visible * (tall ? .70 : .40)
      const gap = tall ? 5 : 7
      const squeeze = tall ? 1.7 : 1
      const wash = Math.min(170, Math.round(width * .22))
      const playhead = Math.round(width * .5)
      const playheadTop = fullHeight ? 0 : mid - peak * 1.18
      const playheadBottom = fullHeight ? height : mid + peak * 1.18
      const scroll = currentTime * SPEED
      context.lineCap = 'round'

      for (let x = -gap; x <= width + gap; x += gap) {
        const position = (x + scroll) * squeeze
        const inMoment = momentAt(position)
        const barHeight = Math.max(4, level(position, currentTime) * peak * (inMoment ? 1.22 : .66))
        const behind = playhead - x
        const glow = inMoment ? (behind >= 0 ? Math.exp(-behind / 260) : Math.exp(behind / 90)) : 0
        const distance = (x - playhead) / 70
        const near = Math.exp(-distance * distance) * .5
        const heat = Math.min(1, glow + near * (inMoment ? 1 : .35))
        context.strokeStyle = heat > .04
          ? `rgba(${palette.accent},${(.22 + heat * .72).toFixed(3)})`
          : green(.16 + level(position, currentTime) * .16)
        context.lineWidth = (1.9 + heat * 1.7) * palette.stroke
        const lift = 1 + heat * .22
        context.beginPath()
        context.moveTo(x, mid - barHeight * lift)
        context.lineTo(x, mid + barHeight * lift)
        context.stroke()
      }

      const glowBand = context.createLinearGradient(playhead - wash, 0, playhead + wash, 0)
      glowBand.addColorStop(0, `rgba(${palette.accent},0)`)
      glowBand.addColorStop(.5, `rgba(${palette.accent},.1)`)
      glowBand.addColorStop(1, `rgba(${palette.accent},0)`)
      context.fillStyle = glowBand
      context.fillRect(playhead - wash, mid - height, wash * 2, height * 2)
      context.strokeStyle = `rgba(${palette.accent},.7)`
      context.lineWidth = 1.6 * palette.stroke
      context.beginPath()
      context.moveTo(playhead, playheadTop)
      context.lineTo(playhead, playheadBottom)
      context.stroke()
      context.fillStyle = `rgba(${palette.accent},.85)`
      context.beginPath()
      context.arc(playhead, playheadTop, 3.2, 0, Math.PI * 2)
      context.fill()
    }

    const tick = (now: number) => {
      if (reducedMotion.matches || document.hidden) {
        frame = null
        return
      }
      if (now - previous >= 1000 / 30) {
        time += Math.min((now - previous) / 1000, .06)
        previous = now
        draw(time)
      }
      frame = window.requestAnimationFrame(tick)
    }

    const restart = () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = null
      previous = performance.now()
      draw(time)
      if (!reducedMotion.matches && !document.hidden) frame = window.requestAnimationFrame(tick)
    }

    const resize = () => {
      const bounds = canvas.getBoundingClientRect()
      width = bounds.width
      height = bounds.height
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      context.setTransform(dpr, 0, 0, dpr, 0, 0)
      palette = readPalette(canvas)
      draw(time)
    }

    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    const themeObserver = new MutationObserver(() => { palette = readPalette(canvas); draw(time) })
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    reducedMotion.addEventListener('change', restart)
    document.addEventListener('visibilitychange', restart)
    resize()
    restart()

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      observer.disconnect()
      themeObserver.disconnect()
      reducedMotion.removeEventListener('change', restart)
      document.removeEventListener('visibilitychange', restart)
    }
  }, [fullHeight])

  return <canvas ref={canvasRef} className="motion-access-timeline" aria-hidden="true" />
}

export default MotionTimeline
